import { FC } from "react";
import { P1 } from "components/atoms/Paragraph";
import { Page } from "components/templates/Page";
import { TabSwitcherProducts } from "components/templates/TabSwitcherProducts";
import { DesktopSpacer, Spacer } from "components/templates/Spacer";
import { HeroSection } from "components/organisms/HeroSection";
import { NewZigZag } from "components/molecules/NewZigZag";
import { QuoteCarousel } from "components/organisms/QuoteCarousel";
import { H2 } from "components/atoms/Heading";
import { NewProductIntro } from "components/atoms/NewProductIntro";
import BottomSection from "components/organisms/BottomSection";
import { KeyResourcesItemProps } from "components/atoms/KeyResourcesItem";
import {
  ProductOverviewSectionCopy,
  ProductOverviewSection,
  ProductCardsSection,
  ButtonWrapper,
  YuCoinImg,
  GuardianImg,
  CenteredButtonWrap,
} from "./styles";
import { Clouds } from "./Clouds";
import { color } from "@styles";
import { CallOutAd } from "components/molecules/CallOutAd";
import { ComparisonTableBulletPoints } from "components/molecules/ComparisonTableBulletPoints";
import { ProductCard } from "components/molecules/ProductCard";
import { Link } from "@components/atoms";
import { BetterButton } from "components/atoms/BetterButton";
import { CallbackAppOnly } from "components/molecules/CallbackAppOnly";
import { useLocale } from "hooks";
import { useLocaleData } from "hooks/useLocaleData";
import data from "./translations";
import { useTranslations } from "next-intl";
import { LatestCaseStudy } from "utils/server";
import { ProductCardDimensions, getProductCardDimensions } from "@utils";
import { StaticRoutes } from "@routes";

interface GroupInsuranceProps {
  keyResources: KeyResourcesItemProps[];
  latestCaseStudy: LatestCaseStudy;
}

const GroupInsurance: FC<GroupInsuranceProps> = ({ keyResources, latestCaseStudy }) => {
  const { locale } = useLocale();
  const { products, quotes, yumojiImage, prodSection, tabs } = useLocaleData(data);
  const t = useTranslations("group-insurance");

  const PRODUCT_CARD_DIMENSIONS: ProductCardDimensions = {
    us: {
      height: "20rem",
      width: "16rem",
      justifyContent: "space-around",
    },
    za: {
      height: "22.5rem",
      width: "22rem",
      justifyContent: "flex-start",
    },
    uk: {
      height: "29.625rem",
      width: "16rem",
      justifyContent: "flex-start",
    },
  };

  return (
    <>
      <Page
        navColor="transparent"
        latestCaseStudy={latestCaseStudy}
        hero={
          <HeroSection
            eyeBrow={t<string>("hero.eyebrow")}
            title={t<string>("hero.title")}
            subHeading={t<string>("hero.subheading")}
            backgroundColor={color.S410}
            buttonText={t<string>("hero.button-text")}
            image="/static/giraffes/YugiJumpLeft.svg"
            clouds={<Clouds />}
            statBar={{
              heading: t<string>("hero.stat-bar.heading"),
              tooltipCopy: "Trustpilot.com",
              backgroundColor: color.S3100,
              stats: [
                {
                  value: t<string>("hero.stat-bar.stats.stat1.value"),
                  description: t<string>("hero.stat-bar.stats.stat1.description"),
                  tooltip: t<string>("hero.stat-bar.stats.stat1.tooltip"),
                },
                {
                  value: t<string>("hero.stat-bar.stats.stat2.value"),
                  description: t<string>("hero.stat-bar.stats.stat2.description"),
                  tooltip: t<string>("hero.stat-bar.stats.stat2.tooltip"),
                },
                {
                  value: t<string>("hero.stat-bar.stats.stat3.value"),
                  description: t<string>("hero.stat-bar.stats.stat3.description"),
                  tooltip: t<string>("hero.stat-bar.stats.stat3.tooltip"),
                },
              ],
            }}
          />
        }
        fullScreenHero
      >
        <NewProductIntro
          title={prodSection.title}
          description={prodSection.copy}
          maxWidth="56rem"
          pt={locale === "za" ? "7rem" : ""}
          pb={locale === "za" ? "7rem" : ""}
        />
        <ProductOverviewSectionCopy>
          <H2
            align="center"
            pt="7rem"
            fontSize="3rem"
            style={{ maxWidth: "48rem", margin: "0 auto" }}
          >
            {t<string>("products.title")}
          </H2>
          <P1
            align="center"
            fontSize="1.25rem"
            lineHeight="2rem"
            style={{ maxWidth: "58rem", margin: "0 auto" }}
          >
            {t<string>("products.description")}
          </P1>
        </ProductOverviewSectionCopy>
        <ProductOverviewSection>
          <ProductCardsSection>
            {products
              .filter((item) => item.isLive === undefined || item.isLive === true)
              .map((product) => {
                return (
                  <ProductCard
                    key={`key_product_card_${product.iconName}`}
                    title={product.title}
                    iconName={product.iconName}
                    description={product.description}
                    href={product.href}
                    width={getProductCardDimensions("width", locale, PRODUCT_CARD_DIMENSIONS)}
                    height={getProductCardDimensions("height", locale, PRODUCT_CARD_DIMENSIONS)}
                    justifyContent={getProductCardDimensions(
                      "justifyContent",
                      locale,
                      PRODUCT_CARD_DIMENSIONS,
                    )}
                    hasPadding={locale === "us" ? false : true}
                  />
                );
              })}
          </ProductCardsSection>
        </ProductOverviewSection>
        {locale === "us" ? (
          <>
            <CenteredButtonWrap>
              <Link href={StaticRoutes.getAQuote}>
                <BetterButton text="Speak to our team" />
              </Link>
            </CenteredButtonWrap>
            <Spacer height="6.5rem" />
          </>
        ) : (
          <NewZigZag
            title="Always-on access to insurance"
            description="Empower employees with easy access to policy information, so they know what cover they have, and how it works. Give them clear visibility of the YuCoin they can earn, shown on the Yu Screen, to encourage them to participate in quests and challenges, and boost their health!"
            align="left"
            pt={locale === "za" ? "10rem" : "8rem"}
            pb={locale === "za" ? "9rem" : "0rem"}
            reverse
          >
            {yumojiImage?.imageDetails}
          </NewZigZag>
        )}
        {locale === "uk" ? (
          <CallOutAd
            title={t<string>("call-out-ad.title")}
            subtitle={t<string>("call-out-ad.subtitle")}
            backgroundColor={color.S410}
            borderColor={color.S4100}
          >
            <YuCoinImg
              src="/static/pages/group-insurance/YuCoin-power.gif"
              alt="YuCoin power"
              width={250}
              height={250}
            />
          </CallOutAd>
        ) : null}
        {locale === "us" ? null : (
          <>
            <ComparisonTableBulletPoints
              comparison={[
                "Group Insurance",
                "Employee Assistance Programme",
                "Virtual GP",
                "Wellbeing and benefits platform",
                "Gamified wellness challenges",
                "Rewards for healthy behaviour (e.g. walking, meditating)",
                "ESG, CSR and social impact donations (eg. tree planting)",
                "Wellness product discounts",
                "Dynamic wellbeing event system",
                "Engagement and health impact reports",
                "Customised Wellbeing Hub",
                "Self-service HR Portal",
                "Self-service campaign management",
              ]}
            />
            <ButtonWrapper>
              <Link href="/get-a-quote/">
                <BetterButton text="Speak to our team" width="16rem" />
              </Link>
            </ButtonWrapper>
          </>
        )}
        <DesktopSpacer height="2.5rem" background="#F4F0FF" />
        <TabSwitcherProducts
          title={t<string>("tab-switcher.title")}
          content={t<string>("tab-switcher.content")}
          tabs={tabs ? tabs : null}
        />
        <QuoteCarousel slides={quotes} />
        <BottomSection keyResources={keyResources}>
          {locale === "za" ? null : (
            <>
              <CallbackAppOnly
                title={t<string>("callback-app-only.title")}
                subtitle={t<string>("callback-app-only.subtitle")}
                buttonText={t<string>("callback-app-only.cta")}
              />
            </>
          )}
        </BottomSection>
      </Page>
    </>
  );
};

export default GroupInsurance;
